// Pianifica (ex Attività): i lavori che PopTown sa fare e ripetere. Ognuna parte a mano, con un input o a orario
// (schedule di Temporal); ogni giro apre una sessione vera. Si creano e si modificano nella
// pagina «Nuova attività».
import { useCallback, useEffect, useState } from 'react';
import { excerpt } from '../../lib/excerpt';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import type { Pipeline, Routine } from '../../lib/api';
import type { SessionState } from '../../types';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Icon } from '../../components/Icon';
import { PageHeader } from '../../components/PageHeader';
import { Pop } from '../../components/Pop';
import { StatusPill } from '../../components/StatusPill';
import { useStore } from '../../store/StoreProvider';
import { RoutinePage } from './RoutinePage';
import { RunWithInput } from './RunWithInput';
import { describeWhen, when } from './when';

function iconOf(r: Routine): string {
  if (r.pipeline === 'development') return 'rocket';
  if (r.trigger === 'schedule') return 'calendar-clock';
  if (r.trigger === 'condition') return 'zap';
  if (r.trigger === 'hook') return 'plug-zap';
  return r.trigger === 'input' ? 'search' : 'play';
}

const TINTS = ['blue', 'mint', 'coral', 'lilac', 'sun', 'pink'];

function errorText(e: unknown, fallback: string): string {
  return e instanceof ApiError && e.detail ? e.detail : fallback;
}

export function Routines() {
  const { openModal, toast } = useStore();
  // i nomi dei progetti, per le attività di progetto
  const [projectNames, setProjectNames] = useState<Record<string, string>>({});
  // i pop delle attività, per il loro avatar sulla scheda
  const [faces, setFaces] = useState<Record<string, string>>({});
  useEffect(() => {
    void api.profiles().then(
      (ps) => setFaces(Object.fromEntries(ps.map((p) => [p.id, p.avatar]))),
      () => undefined,
    );
  }, []);
  const avatarOf = (id: string | null) => (id ? (faces[id] ?? null) : null);
  useEffect(() => {
    void api.projects().then(
      (r) => setProjectNames(Object.fromEntries(r.projects.map((p) => [p.id, p.name]))),
      () => undefined,
    );
  }, []);
  const nav = useNavigate();
  const [rows, setRows] = useState<Routine[] | null>(null);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [wizard, setWizard] = useState<{ editing?: Routine } | null>(null);
  const [asking, setAsking] = useState<Routine | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setRows(await api.routines());
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Non riesco a leggere le attività.'));
    }
  }, []);

  useEffect(() => {
    void refresh();
    void api.pipelines().then(setPipelines, () => setPipelines([]));
    // i giri a orario partono da soli: l'elenco si rinfresca ogni mezzo minuto
    const timer = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const run = async (r: Routine, input?: string) => {
    try {
      const { id } = await api.runRoutine(r.id, input);
      toast({ kind: 'success', title: `${r.name}: avviata` });
      nav(`/s/${id}`);
    } catch (e) {
      toast({ kind: 'danger', title: 'Non è partita', body: errorText(e, 'Riprova tra poco.') });
    }
  };

  const toggle = async (r: Routine) => {
    try {
      await api.enableRoutine(r.id, !r.enabled);
      await refresh();
    } catch (e) {
      toast({ kind: 'danger', title: 'Non riesco a cambiarla', body: errorText(e, '') });
    }
  };

  const remove = (r: Routine) =>
    openModal({
      kind: 'confirm',
      title: `Eliminare «${r.name}»?`,
      okLabel: 'Elimina',
      danger: true,
      body: 'La pianificazione sparisce; le sessioni già fatte restano in Conversazioni.',
      payload: {
        run: async () => {
          await api.deleteRoutine(r.id);
          await refresh();
        },
      },
    });

  const kindName = (id: string) => pipelines.find((p) => p.id === id)?.name ?? id;
  // creare o modificare un'attività apre la sua pagina al posto dell'elenco
  if (wizard)
    return (
      <RoutinePage
        pipelines={pipelines}
        editing={wizard.editing}
        onClose={() => setWizard(null)}
        onSaved={() => void refresh()}
        onRun={(r) => void run(r)}
      />
    );
  return (
    <div className="page">
      <PageHeader
        title="Pianifica"
        subtitle="I lavori che PopTown sa fare e ripetere: a mano, con un input o a orario."
        actions={
          // con l'elenco vuoto il pulsante sta solo al centro, nel messaggio
          rows && rows.length > 0 ? (
            <Button variant="primary" icon="plus" onClick={() => setWizard({})}>
              Nuova attività
            </Button>
          ) : undefined
        }
      />
      {error && <Banner kind="danger">{error}</Banner>}
      {rows && rows.length === 0 && (
        <EmptyState
          icon="calendar-clock"
          pop="scheduler-01"
          title="Nessuna attività."
          hint="Un'attività è un lavoro che PopTown ripete: un riassunto ogni mattina, il meteo ogni 3 ore, una ricerca quando la chiedi."
          action={
            <Button variant="primary" icon="plus" onClick={() => setWizard({})}>
              Nuova attività
            </Button>
          }
        />
      )}
      <div className="plan-grid">
        {rows?.map((r, i) => {
          const pop = avatarOf(r.options?.profile ?? null);
          const tint = TINTS[i % TINTS.length];
          return (
            <section
              key={r.id}
              className="plan-card"
              data-tint={tint}
              data-off={!r.enabled || undefined}
              aria-label={r.name}
            >
              <div className="plan-card-head">
                <span className="dash-face">
                  {pop ? <Pop avatar={pop} size={58} /> : <Icon name={iconOf(r)} size={24} />}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="plan-card-name trunc">{r.name}</span>
                  <span className="t-caption faint trunc" style={{ display: 'block' }}>
                    {r.options?.kind === 'operational' ? 'Operativa' : 'Attività'} ·{' '}
                    {kindName(r.pipeline)}
                  </span>
                </span>
                {r.trigger === 'schedule' && (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={r.enabled}
                    aria-label={r.enabled ? `Metti in pausa ${r.name}` : `Riprendi ${r.name}`}
                    className="pop-switch"
                    data-on={r.enabled || undefined}
                    onClick={() => void toggle(r)}
                  >
                    <span />
                  </button>
                )}
              </div>
              <div className="plan-card-task clamp2" title={r.task}>
                {excerpt(r.task)}
              </div>
              <div className="plan-card-tags">
                <span className="plan-tag" data-strong>
                  {describeWhen(r.trigger, r.schedule, r.inputHint)}
                </span>
                {r.project && (
                  <Link to={`/progetti/${r.project}`} className="plan-tag">
                    progetto {projectNames[r.project] ?? ''}
                  </Link>
                )}
                {!r.enabled && <span className="plan-tag">in pausa</span>}
                {r.enabled && r.nextRun && (
                  <span className="plan-tag">prossimo {when(r.nextRun)}</span>
                )}
              </div>
              <div className="plan-card-foot">
                {/* con una base, sulla scheda stretta i bottoni vanno a capo invece di
                    schiacciare la scritta a una parola per riga (2026-11-02) */}
                <span className="t-caption faint" style={{ flex: '1 1 150px', minWidth: 0 }}>
                  {r.lastRun ? `Ultimo giro ${when(r.lastRun.at)}` : 'Mai avviata'}
                </span>
                {/* lo stato e i comandi vanno a capo insieme, non uno alla volta */}
                <span className="plan-card-actions">
                  {r.lastRun && (
                    <button
                      type="button"
                      onClick={() => nav(`/s/${r.lastRun?.sessionId}`)}
                      title="Apri l'ultimo giro"
                      style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                    >
                      <StatusPill state={r.lastRun.state as SessionState} size="sm" />
                    </button>
                  )}
                  <Button
                    variant="icon"
                    icon="pencil"
                    aria-label={`Modifica ${r.name}`}
                    title="Modifica"
                    onClick={() => setWizard({ editing: r })}
                  />
                  <Button
                    variant="icon"
                    icon="trash"
                    aria-label={`Elimina ${r.name}`}
                    title="Elimina"
                    onClick={() => remove(r)}
                  />
                  <Button
                    icon="play"
                    onClick={() => (r.trigger === 'input' ? setAsking(r) : void run(r))}
                  >
                    Avvia
                  </Button>
                </span>
              </div>
            </section>
          );
        })}
      </div>
      {asking && (
        <RunWithInput
          routine={asking}
          onClose={() => setAsking(null)}
          onRun={(text) => void run(asking, text)}
        />
      )}
    </div>
  );
}
